/**
 * The vendor team's view of an issue's pipeline — one card per stage.
 *
 * A reporter never sees these (the server leaves `analysis`, `build`,
 * `qc_reviews` and `jobs` out of their detail): what the agent found in their
 * data and how the fix was built is the team's working material, and the
 * reporter follows the OUTCOME on the timeline instead.
 *
 * Every decision button here is an entry of `issue.actions` — the events the
 * state machine says this caller may fire now. A card only decides WHERE a
 * button sits (the plan's approval next to the plan, QC's verdict next to the
 * checklist); whether it exists is the server's answer.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bot, CheckCircle2, Cpu, ClipboardCheck, ExternalLink, FileCode2, FlaskConical, GitBranch,
  ListChecks, RefreshCw, ScrollText, Search, ThumbsDown, ThumbsUp, XCircle,
} from 'lucide-react'

import type {
  AgentRunSummary, AgentTranscript, BuildTestRun, IssueAction, IssueAnalysis, IssueBuild, IssueDetail,
  QcChecklistItem, RetryStage, SupportJob, SupportNotify, SupportRuntime,
} from './contract'
import {
  Card, ErrorLine, Pill, Spinner, SupportModal, btn, errorText, fmtDateTime, inputStyle,
  labelStyle, mutedText, riskTone, timeAgo, type Tone,
} from './ui'

/** The stages the retry endpoint accepts (`RetryIn.stage`). */
export const RETRY_STAGES: RetryStage[] = ['triage', 'analyse', 'build', 'test', 'qa']

function pct(v?: number | null): string {
  return typeof v === 'number' ? `${Math.round(v * 100)}%` : '—'
}

function testTone(status?: string | null): Tone {
  const s = (status || '').toLowerCase()
  if (['passed', 'pass', 'ok', 'done', 'success'].includes(s)) return 'success'
  if (['failed', 'fail', 'error'].includes(s)) return 'error'
  if (['skipped', 'not_applicable', 'n/a'].includes(s)) return 'neutral'
  return 'warning'
}

function jobTone(state?: string | null): Tone {
  if (state === 'done') return 'success'
  if (state === 'failed') return 'error'
  if (state === 'running') return 'accent'
  return 'neutral'
}

function ActionButtons({ actions, events, onAction, busy }: {
  actions: IssueAction[]
  events: string[]
  onAction: (a: IssueAction) => void
  busy?: boolean
}) {
  const shown = actions.filter((a) => events.includes(a.event))
  if (!shown.length) return null
  return (
    <>
      {shown.map((a, i) => (
        <button key={a.event} type="button" disabled={busy}
          style={btn(i === 0 ? 'primary' : 'ghost', true)} onClick={() => onAction(a)}>
          {i === 0 ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />} {a.label}
        </button>
      ))}
    </>
  )
}

// ── transcript ─────────────────────────────────────────────────────────────

export function TranscriptButton({ runtime, runId, label = 'Transcript' }: {
  runtime: SupportRuntime
  runId?: number | null
  label?: string
}) {
  const [open, setOpen] = useState(false)
  if (!runtime.transcript || !runId) return null
  return (
    <>
      <button type="button" style={btn('ghost', true)} onClick={() => setOpen(true)}>
        <ScrollText size={12} /> {label}
      </button>
      {open && <TranscriptViewer runtime={runtime} runId={runId} onClose={() => setOpen(false)} />}
    </>
  )
}

export function TranscriptViewer({ runtime, runId, onClose }: {
  runtime: SupportRuntime
  runId: number
  onClose: () => void
}) {
  const [data, setData] = useState<AgentTranscript | null>(null)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')

  useEffect(() => {
    let cancelled = false
    if (!runtime.transcript) return undefined
    runtime.transcript(runId)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(errorText(e, 'The transcript could not be loaded.')) })
    return () => { cancelled = true }
  }, [runtime, runId])

  const lines = useMemo(() => {
    const all = (data?.transcript || '').split('\n')
    const term = q.trim().toLowerCase()
    return term ? all.filter((l) => l.toLowerCase().includes(term)) : all
  }, [data, q])

  return (
    <SupportModal open onClose={onClose} width={860}
      title={data ? `Agent run #${data.id} — ${data.stage}` : `Agent run #${runId}`}>
      <ErrorLine message={error} />
      {!data && !error && <div style={{ display: 'flex', gap: 8 }}><Spinner /> Loading…</div>}
      {data && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
            <Pill tone={jobTone(data.status === 'ok' ? 'done' : data.status)}>{data.status}</Pill>
            {data.engine && <span style={mutedText}>{data.engine}{data.model ? ` · ${data.model}` : ''}</span>}
            {(data.input_tokens || data.output_tokens) ? (
              <span style={mutedText}>
                {data.input_tokens || 0} in · {data.output_tokens || 0} out tokens
              </span>
            ) : null}
            <span style={{ flex: 1 }} />
            <div style={{ position: 'relative', width: 220 }}>
              <Search size={12} style={{ position: 'absolute', left: 8, top: 9, color: 'var(--sp-muted)' }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter lines"
                style={{ ...inputStyle, paddingLeft: 26, fontSize: '0.8425rem' }} />
            </div>
          </div>
          <ErrorLine message={data.error || ''} />
          <pre style={{
            margin: 0, maxHeight: '60vh', overflow: 'auto', fontSize: '0.8125rem', lineHeight: 1.5,
            background: 'var(--sp-bg2)', border: '1px solid var(--sp-border)', borderRadius: 8,
            padding: '0.6rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {lines.length ? lines.join('\n') : (data.transcript ? 'No line matches.' : 'The run left no transcript.')}
          </pre>
        </>
      )}
    </SupportModal>
  )
}

// ── analysis ───────────────────────────────────────────────────────────────

export function AnalysisCard({ analysis, runtime }: {
  analysis: IssueAnalysis
  runtime: SupportRuntime
}) {
  return (
    <Card title={`AI analysis${analysis.version > 1 ? ` (v${analysis.version})` : ''}`}
      icon={<Bot size={15} />}
      actions={(
        <>
          {analysis.risk && (
            <Pill tone={riskTone(analysis.risk)} title={(analysis.risk_reasons || []).join('\n')}>
              {analysis.risk} risk
            </Pill>
          )}
          <Pill tone="accent" title="How sure the agent is of the ROOT CAUSE (not of the fix)">
            confidence {pct(analysis.confidence)}
          </Pill>
          <TranscriptButton runtime={runtime} runId={analysis.agent_run_id} />
        </>
      )}>
      {analysis.summary && <p style={{ margin: '0 0 8px' }}>{analysis.summary}</p>}
      {analysis.root_cause && (
        <div style={{ marginBottom: 8 }}>
          <div style={labelStyle}>Root cause</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{analysis.root_cause}</div>
        </div>
      )}
      {analysis.evidence.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={labelStyle}>Evidence</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {analysis.evidence.map((e, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {e.kind && <Pill>{String(e.kind)}</Pill>}{' '}
                <span style={{ whiteSpace: 'pre-wrap' }}>{String(e.detail ?? JSON.stringify(e))}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {analysis.affected_objects.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={labelStyle}>Affected objects</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {analysis.affected_objects.map((o, i) => (
              <li key={i}>
                <code>{String(o.obj_name || '?')}</code>
                {o.obj_type ? <span style={mutedText}> ({String(o.obj_type)})</span> : null}
                {o.why ? <> — {String(o.why)}</> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      {analysis.risk_reasons.length > 0 && (
        <div>
          <div style={labelStyle}>Why this risk</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {analysis.risk_reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
    </Card>
  )
}

// ── plan ───────────────────────────────────────────────────────────────────

/** Plan-gate events placed on this card (when the server offers them). */
export const PLAN_EVENTS = ['approve_plan', 'request_changes']

export function PlanCard({ analysis, actions, onAction, busy }: {
  analysis: IssueAnalysis
  actions: IssueAction[]
  onAction: (a: IssueAction) => void
  busy?: boolean
}) {
  const plan = analysis.plan || {}
  const steps = plan.steps || []
  const [openStep, setOpenStep] = useState<number | null>(null)
  if (!steps.length && !plan.summary && !plan.plan_summary) return null
  return (
    <Card title="Proposed fix" icon={<ListChecks size={15} />}
      actions={<ActionButtons actions={actions} events={PLAN_EVENTS} onAction={onAction} busy={busy} />}>
      {(plan.plan_summary || plan.summary) && (
        <p style={{ margin: '0 0 8px', whiteSpace: 'pre-wrap' }}>{plan.plan_summary || plan.summary}</p>
      )}
      <ol style={{ margin: 0, paddingLeft: 20 }}>
        {steps.map((s, i) => (
          <li key={s.id || i} style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{s.label || s.target_file || s.obj_name || `Step ${i + 1}`}</span>
              {s.action && <Pill tone={s.action === 'delete' ? 'error' : 'neutral'}>{s.action}</Pill>}
              {s.obj_type && <Pill>{s.obj_type}{s.obj_name ? ` · ${s.obj_name}` : ''}</Pill>}
              {s.instruction && (
                <button type="button" style={{ ...btn('ghost', true), padding: '0 0.4rem' }}
                  onClick={() => setOpenStep(openStep === i ? null : i)}>
                  {openStep === i ? 'Hide detail' : 'Detail'}
                </button>
              )}
            </div>
            {s.target_file && <div style={{ ...mutedText, fontFamily: 'monospace' }}>{s.target_file}</div>}
            {openStep === i && s.instruction && (
              <div style={{
                marginTop: 4, padding: '0.45rem 0.6rem', borderRadius: 6, background: 'var(--sp-bg2)',
                whiteSpace: 'pre-wrap', fontSize: '0.8425rem',
              }}>{s.instruction}</div>
            )}
          </li>
        ))}
      </ol>
      {analysis.test_plan?.acceptance && analysis.test_plan.acceptance.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={labelStyle}>Acceptance criteria</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {analysis.test_plan.acceptance.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}
    </Card>
  )
}

// ── build ──────────────────────────────────────────────────────────────────

function DiffView({ text }: { text: string }) {
  if (!text) return <div style={mutedText}>No textual difference (binary or unchanged).</div>
  return (
    <pre style={{
      margin: 0, maxHeight: 420, overflow: 'auto', fontSize: '0.8125rem', lineHeight: 1.45,
      background: 'var(--sp-bg2)', border: '1px solid var(--sp-border)', borderRadius: 8, padding: '0.4rem 0',
    }}>
      {text.split('\n').map((line, i) => {
        const add = line.startsWith('+') && !line.startsWith('+++')
        const del = line.startsWith('-') && !line.startsWith('---')
        const hunk = line.startsWith('@@')
        return (
          <div key={i} style={{
            padding: '0 0.6rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            color: add ? 'var(--sp-success)' : del ? 'var(--sp-error)' : hunk ? 'var(--sp-accent)' : 'var(--sp-text)',
            background: add ? 'rgba(22,163,74,0.08)' : del ? 'rgba(220,38,38,0.08)' : 'transparent',
          }}>{line || ' '}</div>
        )
      })}
    </pre>
  )
}

function TestRow({ t }: { t: BuildTestRun }) {
  const [open, setOpen] = useState(false)
  const hasDetail = t.details && Object.keys(t.details).length > 0
  return (
    <li style={{ marginBottom: 5 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <Pill tone={testTone(t.status)}>{t.status}</Pill>
        <strong style={{ fontSize: '0.8625rem' }}>{t.suite}</strong>
        {t.summary && <span style={{ fontSize: '0.8625rem' }}>{t.summary}</span>}
        {hasDetail && (
          <button type="button" style={{ ...btn('ghost', true), padding: '0 0.4rem' }}
            onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Details'}</button>
        )}
      </div>
      {open && (
        <pre style={{
          margin: '4px 0 0', maxHeight: 260, overflow: 'auto', fontSize: '0.8125rem',
          background: 'var(--sp-bg2)', padding: '0.4rem 0.6rem', borderRadius: 6,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{JSON.stringify(t.details, null, 2)}</pre>
      )}
    </li>
  )
}

export function BuildCard({ build, runtime, number }: {
  build: IssueBuild
  runtime: SupportRuntime
  number: number
}) {
  const [path, setPath] = useState<string | null>(null)
  const [diff, setDiff] = useState<string>('')
  const [diffBusy, setDiffBusy] = useState(false)
  const [diffError, setDiffError] = useState('')

  const openDiff = useCallback(async (p: string) => {
    if (path === p) { setPath(null); return }
    if (!runtime.diff) return
    setPath(p)
    setDiff('')
    setDiffError('')
    setDiffBusy(true)
    try {
      const res = await runtime.diff(number, p)
      setDiff(res.diff || '')
    } catch (e) {
      setDiffError(errorText(e, 'The difference could not be loaded.'))
    } finally {
      setDiffBusy(false)
    }
  }, [path, runtime, number])

  const stats = build.diff_stats || {}
  return (
    <Card title={`Build${build.attempt > 1 ? ` — attempt ${build.attempt}` : ''}`}
      icon={<GitBranch size={15} />}
      actions={(
        <>
          {build.status && <Pill tone={testTone(build.status)}>{build.status}</Pill>}
          <TranscriptButton runtime={runtime} runId={build.agent_run_id} />
        </>
      )}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px', fontSize: '0.8625rem', marginBottom: 8 }}>
        {build.branch && <><span style={mutedText}>Branch</span><code>{build.branch}</code></>}
        {build.base_commit && (
          <><span style={mutedText}>Commits</span>
            <code>{build.base_commit.slice(0, 8)} → {(build.head_commit || 'HEAD').slice(0, 8)}</code></>
        )}
        {build.staging_db && <><span style={mutedText}>Staging DB</span><code>{build.staging_db}</code></>}
        {typeof stats.files === 'number' && (
          <><span style={mutedText}>Changes</span>
            <span>{stats.files} file(s) · <span style={{ color: 'var(--sp-success)' }}>+{stats.insertions || 0}</span>{' '}
              <span style={{ color: 'var(--sp-error)' }}>−{stats.deletions || 0}</span></span></>
        )}
        {typeof build.correction_cycles === 'number' && build.correction_cycles > 0 && (
          <><span style={mutedText}>Corrections</span><span>{build.correction_cycles}</span></>
        )}
        {build.finished_at && <><span style={mutedText}>Finished</span><span>{fmtDateTime(build.finished_at)}</span></>}
      </div>
      <ErrorLine message={build.error || ''} />

      {build.changed_files.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={labelStyle}>Changed files</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {build.changed_files.map((f) => (
              <li key={f.path} style={{ marginBottom: 3 }}>
                <button type="button" disabled={!runtime.diff} onClick={() => { void openDiff(f.path) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
                    background: path === f.path ? 'var(--sp-surface)' : 'transparent',
                    border: 'none', cursor: runtime.diff ? 'pointer' : 'default', padding: '2px 4px',
                    borderRadius: 6, color: 'var(--sp-text)',
                  }}>
                  <FileCode2 size={12} />
                  <Pill tone={f.status?.startsWith('D') ? 'error' : f.status?.startsWith('A') ? 'success' : 'neutral'}>
                    {f.status || 'M'}
                  </Pill>
                  <code style={{ fontSize: '0.8225rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.path}</code>
                </button>
                {path === f.path && (
                  <div style={{ margin: '4px 0 8px' }}>
                    {diffBusy && <div style={{ display: 'flex', gap: 6 }}><Spinner size={13} /> Loading diff…</div>}
                    <ErrorLine message={diffError} />
                    {!diffBusy && !diffError && <DiffView text={diff} />}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {build.tests.length > 0 && (
        <div>
          <div style={labelStyle}><FlaskConical size={11} style={{ verticalAlign: -1 }} /> Automated tests</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {build.tests.map((t) => <TestRow key={t.id} t={t} />)}
          </ul>
        </div>
      )}
    </Card>
  )
}

// ── QC ─────────────────────────────────────────────────────────────────────

/** QC-verdict events placed on this card (when the server offers them). */
export const QC_EVENTS = ['qc_pass', 'qc_fail']

export function QcCard({ issue, runtime, onSaved, onOpenStaging, notify }: {
  issue: IssueDetail
  runtime: SupportRuntime
  onSaved: (issue: IssueDetail) => void
  onOpenStaging?: (issueNumber: number) => void
  notify?: SupportNotify
}) {
  const source = issue.analysis?.test_plan?.qc_checklist || []
  const [items, setItems] = useState<QcChecklistItem[]>(() => source.map((item) => ({ item, done: false })))
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState<'pass' | 'fail' | null>(null)
  const [error, setError] = useState('')

  // A new analysis brings a new checklist; ticks on the old one do not carry over.
  const sourceKey = source.join('\u0001')
  useEffect(() => {
    setItems(source.map((item) => ({ item, done: false })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey])

  const canPass = issue.actions.some((a) => a.event === 'qc_pass')
  const canFail = issue.actions.some((a) => a.event === 'qc_fail')
  const canDecide = !!runtime.qc && (canPass || canFail)
  const reviews = issue.qc_reviews || []
  if (!canDecide && !reviews.length && !source.length) return null

  const decide = async (verdict: 'pass' | 'fail') => {
    if (!runtime.qc) return
    if (verdict === 'fail' && !notes.trim()) {
      setError('Say what failed, so the fix can be corrected.')
      return
    }
    if (verdict === 'pass' && items.some((i) => !i.done)
        && !window.confirm('Not every checklist item is ticked. Pass the fix anyway?')) {
      return
    }
    setBusy(verdict)
    setError('')
    try {
      const next = await runtime.qc(issue.number, { verdict, checklist: items, notes: notes.trim() || undefined })
      notify?.success(verdict === 'pass' ? 'QC passed.' : 'QC failed — sent back for correction.')
      setNotes('')
      onSaved(next)
    } catch (e) {
      setError(errorText(e, 'The QC result could not be saved.'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card title="Quality control" icon={<ClipboardCheck size={15} />}
      actions={onOpenStaging && issue.build ? (
        <button type="button" style={btn('subtle', true)} onClick={() => onOpenStaging(issue.number)}
          title="Open the application on this fix's staging copy (sample data only)">
          <ExternalLink size={12} /> Open staging
        </button>
      ) : undefined}>
      {items.length > 0 ? (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
          {items.map((it, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: canDecide ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={it.done} disabled={!canDecide}
                  onChange={(e) => setItems((cur) => cur.map((x, j) => (j === i ? { ...x, done: e.target.checked } : x)))}
                  style={{ marginTop: 3 }} />
                <span style={{ textDecoration: it.done ? 'line-through' : 'none', opacity: it.done ? 0.7 : 1 }}>
                  {it.item}
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ ...mutedText, marginBottom: 8 }}>
          The analysis wrote no QC checklist — test against the acceptance criteria above.
        </div>
      )}

      {canDecide && (
        <>
          <label style={labelStyle} htmlFor={`twsp-qc-notes-${issue.number}`}>Notes / evidence</label>
          <textarea id={`twsp-qc-notes-${issue.number}`} value={notes} rows={3}
            onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }}
            placeholder="What you tested, and — for a fail — what went wrong" />
          <ErrorLine message={error} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            {canFail && (
              <button type="button" style={btn('danger')} disabled={!!busy} onClick={() => { void decide('fail') }}>
                {busy === 'fail' ? <Spinner size={13} /> : <XCircle size={14} />} Fail
              </button>
            )}
            {canPass && (
              <button type="button" style={btn('success')} disabled={!!busy} onClick={() => { void decide('pass') }}>
                {busy === 'pass' ? <Spinner size={13} /> : <CheckCircle2 size={14} />} Pass
              </button>
            )}
          </div>
        </>
      )}

      {reviews.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={labelStyle}>Previous QC results</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {reviews.map((r) => (
              <li key={r.id} style={{ marginBottom: 4, fontSize: '0.8625rem' }}>
                <Pill tone={r.verdict === 'pass' ? 'success' : 'error'}>{r.verdict}</Pill>{' '}
                <span style={mutedText}>{timeAgo(r.at)}</span>
                {r.notes && <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{r.notes}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

// ── jobs ───────────────────────────────────────────────────────────────────

export function JobsCard({ jobs, runtime, number, canRetry, stages, notify, onQueued }: {
  jobs: SupportJob[]
  runtime: SupportRuntime
  number: number
  canRetry: boolean
  /** The stages that may run in the issue's current state (server's answer);
   *  absent (an older server) → every stage is offered and the server refuses. */
  stages?: RetryStage[]
  notify?: SupportNotify
  onQueued: () => void
}) {
  const offered = stages ?? RETRY_STAGES
  const [stage, setStage] = useState<RetryStage>(offered[0] || 'triage')
  useEffect(() => {
    if (offered.length && !offered.includes(stage)) setStage(offered[0])
  }, [offered, stage])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const retryable = canRetry && !!runtime.retry && offered.length > 0

  const retry = async (s: RetryStage) => {
    if (!runtime.retry) return
    setBusy(true)
    setError('')
    try {
      await runtime.retry(number, s)
      notify?.success(`Re-running ${s}.`)
      onQueued()
    } catch (e) {
      setError(errorText(e, `Could not re-run ${s}.`))
    } finally {
      setBusy(false)
    }
  }

  if (!jobs.length && !retryable) return null
  return (
    <Card title="Pipeline runs" icon={<RefreshCw size={15} />}
      actions={retryable ? (
        <>
          <select value={stage} onChange={(e) => setStage(e.target.value as RetryStage)}
            style={{ ...inputStyle, width: 'auto', padding: '0.2rem 0.4rem', fontSize: '0.8125rem' }}
            aria-label="Stage to re-run">
            {offered.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" style={btn('ghost', true)} disabled={busy} onClick={() => { void retry(stage) }}>
            {busy ? <Spinner size={12} /> : <RefreshCw size={12} />} Re-run stage
          </button>
        </>
      ) : undefined}>
      <ErrorLine message={error} />
      {jobs.length === 0 ? <div style={mutedText}>No pipeline runs yet.</div> : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {jobs.map((j) => (
            <li key={j.id} style={{ marginBottom: 5, fontSize: '0.8625rem' }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <Pill tone={jobTone(j.state)}>{j.state}</Pill>
                <strong>{j.kind}</strong>
                <span style={mutedText}>
                  {j.attempts > 1 ? `${j.attempts} attempts · ` : ''}{timeAgo(j.finished_at || j.created_at)}
                </span>
                {retryable && j.state === 'failed' && (offered as string[]).includes(j.kind) && (
                  <button type="button" style={{ ...btn('ghost', true), padding: '0 0.45rem' }} disabled={busy}
                    onClick={() => { void retry(j.kind as RetryStage) }}>Retry</button>
                )}
              </div>
              {j.last_error && (
                <div style={{ color: 'var(--sp-error)', fontSize: '0.8225rem', marginTop: 2, whiteSpace: 'pre-wrap' }}>
                  {j.last_error}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

// ── agent runs ─────────────────────────────────────────────────────────────

/** Every agent run of the issue's pipeline, each with its transcript. */
export function AgentRunsCard({ runs, runtime }: {
  runs: AgentRunSummary[]
  runtime: SupportRuntime
}) {
  return (
    <Card title="Agent runs" icon={<Cpu size={15} />}>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {runs.slice().reverse().map((r) => (
          <li key={r.id} style={{ marginBottom: 5, fontSize: '0.8625rem' }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <Pill tone={jobTone(r.status === 'ok' || r.status === 'succeeded' ? 'done' : r.status)}>{r.status}</Pill>
              <strong>{r.stage}</strong>
              <span style={mutedText}>
                {[r.engine, r.model].filter(Boolean).join(' · ')}
                {r.finished_at || r.started_at ? ` · ${timeAgo(r.finished_at || r.started_at)}` : ''}
              </span>
              <span style={{ flex: 1 }} />
              <TranscriptButton runtime={runtime} runId={r.id} />
            </div>
            {r.error && (
              <div style={{ color: 'var(--sp-error)', fontSize: '0.8225rem', marginTop: 2, whiteSpace: 'pre-wrap' }}>
                {r.error}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  )
}
